/* ============================================================
   HyperFocus Athletics — Order Wizard
   - Step navigation with validation
   - localStorage progress save / restore (7-day expiry)
   - Cloudinary file uploads
   - Video preview modal (YouTube / Vimeo / direct .mp4)
   - Shopify cart submission → /checkout redirect
   ============================================================ */
(function () {
  'use strict';

  const TOTAL_STEPS = 14;
  const REVIEW_STEP = 14;
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const STORAGE_KEY = 'hf-wiz-progress-v3';
  const STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

  function initWizard(root) {
    if (!root || root.dataset.hfWizInitialized === 'true') return;
    root.dataset.hfWizInitialized = 'true';

    // ─── Config (from data attributes) ─────────────────────────
    const cfg = {
      baseVariantId: root.dataset.baseVariantId,
      basePrice: parseFloat(root.dataset.basePrice) || 0,
      jerseyVariantId: root.dataset.jerseyVariantId,
      jerseyPrice: parseFloat(root.dataset.jerseyPrice) || 0,
      statementVariantId: root.dataset.statementVariantId,
      statementPrice: parseFloat(root.dataset.statementPrice) || 0,
      rushVariantId: root.dataset.rushVariantId,
      rushPrice: parseFloat(root.dataset.rushPrice) || 0,
      cloudinaryCloud: root.dataset.cloudinaryCloud,
      cloudinaryPreset: root.dataset.cloudinaryPreset
    };

    // ─── State ─────────────────────────────────────────────────
    const state = {
      step: 1,
      answers: {
        firstName: '', lastName: '', email: '',
        sport: '', position: '', jerseyNumber: '',
        school: '', conference: '', gradClass: '',
        graphicStyle: '',
        athletePhotoUrl: '', athletePhotoName: '',
        additionalPhotoUrl: '', additionalPhotoName: '',
        signaturePhotoUrl: '', signaturePhotoName: '',
        jerseySwap: '', statementGraphic: '', statementText: '', rushTurnaround: ''
      }
    };

    // ─── Element refs ──────────────────────────────────────────
    const stepEls = Array.from(root.querySelectorAll('[data-hf-wiz-step]'));
    const progressFill = root.querySelector('[data-hf-wiz-progress-fill]');
    const stepCurrentEl = root.querySelector('[data-hf-wiz-step-current]');
    const stepTotalEl = root.querySelector('[data-hf-wiz-step-total]');
    const resetBtn = root.querySelector('[data-hf-wiz-reset]');
    const prevBtn = root.querySelector('[data-hf-wiz-prev]');
    const nextBtn = root.querySelector('[data-hf-wiz-next]');
    const navEl = root.querySelector('[data-hf-wiz-nav]');
    const submitBtn = root.querySelector('[data-hf-wiz-submit]');
    const submitErrorEl = root.querySelector('[data-hf-wiz-submit-error]');
    const totalEl = root.querySelector('[data-hf-wiz-total]');
    const runningTotalEl = root.querySelector('[data-hf-wiz-running-total]');
    const runningTotalWrap = root.querySelector('[data-hf-wiz-running-total-wrap]');

    let lastTotal = null;

    if (stepTotalEl) stepTotalEl.textContent = TOTAL_STEPS;

    // ─── localStorage: save / load / clear ─────────────────────
    function saveProgress() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          step: state.step,
          answers: state.answers,
          savedAt: Date.now()
        }));
        if (resetBtn) resetBtn.hidden = state.step <= 1 && isAnswersEmpty();
      } catch (e) { /* localStorage disabled — silently ignore */ }
    }

    function loadProgress() {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (!saved) return null;
        const parsed = JSON.parse(saved);
        if (!parsed || !parsed.answers) return null;
        if (parsed.savedAt && Date.now() - parsed.savedAt > STORAGE_TTL_MS) {
          clearProgress();
          return null;
        }
        return parsed;
      } catch (e) { return null; }
    }

    function clearProgress() {
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    }

    function isAnswersEmpty() {
      return Object.values(state.answers).every(v => !v);
    }

    // ─── Restore UI from saved state ───────────────────────────
    function restoreUI() {
      // Inputs
      Object.keys(state.answers).forEach(key => {
        const input = root.querySelector(`[data-hf-wiz-input="${key}"]`);
        if (input && state.answers[key]) input.value = state.answers[key];
      });

      // Card selections
      const selectKeys = ['sport', 'gradClass', 'graphicStyle', 'jerseySwap', 'statementGraphic', 'rushTurnaround'];
      selectKeys.forEach(key => {
        const value = state.answers[key];
        if (!value) return;
        const btns = root.querySelectorAll(`[data-hf-wiz-select="${key}"]`);
        btns.forEach(btn => {
          if (btn.dataset.value === value) btn.classList.add('is-selected');
        });
      });

      // Statement text reveal + character count
      if (statementTextWrap) {
        statementTextWrap.hidden = !state.answers.statementGraphic || state.answers.statementGraphic === 'No';
      }
      if (charCountEl && state.answers.statementText) {
        charCountEl.textContent = state.answers.statementText.length;
        if (charCountWrap) charCountWrap.classList.toggle('is-warning', state.answers.statementText.length > 900);
      }

      // File uploads (works with Cloudinary URLs which persist)
      ['athletePhoto', 'additionalPhoto'].forEach(key => {
        const url = state.answers[key + 'Url'];
        const name = state.answers[key + 'Name'];
        if (!url) return;
        const uploadEl = root.querySelector(`[data-hf-wiz-upload="${key}"]`);
        if (!uploadEl) return;
        const previewImg = uploadEl.querySelector('[data-hf-wiz-upload-preview]');
        const filenameEl = uploadEl.querySelector('[data-hf-wiz-upload-filename]');
        const preview = uploadEl.querySelector('.hf-wiz__upload-preview');
        uploadEl.classList.add('has-file');
        if (previewImg) previewImg.src = url;
        if (filenameEl) filenameEl.textContent = name || 'Restored upload';
        if (preview) preview.hidden = false;
      });
    }

    // ─── Step navigation ───────────────────────────────────────
    function goToStep(n) {
      n = Math.max(1, Math.min(TOTAL_STEPS, n));
      state.step = n;

      stepEls.forEach((el) => {
        const stepNum = parseInt(el.dataset.hfWizStep, 10);
        el.classList.toggle('is-active', stepNum === n);
      });

      if (progressFill) progressFill.style.width = `${(n / TOTAL_STEPS) * 100}%`;
      if (stepCurrentEl) stepCurrentEl.textContent = n;
      if (prevBtn) prevBtn.disabled = n === 1;
      if (navEl) navEl.classList.toggle('is-hidden', n === REVIEW_STEP);

      if (nextBtn) {
        const labelSpan = nextBtn.querySelector('span:not(.hf-wiz__nav-arrow)');
        if (labelSpan) labelSpan.textContent = n === TOTAL_STEPS - 1 ? 'Review' : 'Next';
      }

      // Show "Start Over" once user has progressed beyond step 1 or has any answers
      if (resetBtn) resetBtn.hidden = n <= 1 && isAnswersEmpty();

      if (n === REVIEW_STEP) renderReview();

      updateRunningTotal();
      saveProgress();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // ─── Validation ────────────────────────────────────────────
    function validateStep(n) {
      root.querySelectorAll('.hf-wiz__field.is-invalid').forEach((el) => el.classList.remove('is-invalid'));
      const a = state.answers;
      switch (n) {
        case 1: {
          let ok = true;
          if (!a.firstName.trim()) { flagInvalid('firstName'); ok = false; }
          if (!a.lastName.trim()) { flagInvalid('lastName'); ok = false; }
          if (!EMAIL_RE.test(a.email.trim())) { flagInvalid('email'); ok = false; }
          return ok;
        }
        case 2: return !!a.sport;
        case 3: return true;  // Position & Number optional (some sports don't use)
        case 4: { const ok = !!a.school.trim(); if (!ok) flagInvalid('school'); return ok; }
        case 5: { const ok = !!a.conference.trim(); if (!ok) flagInvalid('conference'); return ok; }
        case 6: return !!a.gradClass;
        case 7: return !!a.graphicStyle;
        case 8: return !!a.athletePhotoUrl;    // Athlete photo — required
        case 9: return true;                    // Additional photo — optional
        case 10: return true;                   // Signature — optional (photo OR typed name OR skip)
        case 11: return !!a.jerseySwap;         // Jersey Swap
        case 12: {                              // Statement graphic (add-on)
          if (!a.statementGraphic) return false;
          if (a.statementGraphic !== 'No' && !a.statementText.trim()) { flagInvalid('statementText'); return false; }
          return true;
        }
        case 13: return !!a.rushTurnaround;    // Rush turnaround
        default: return true;                  // Review (14) and any others
      }
    }

    function flagInvalid(inputName) {
      const input = root.querySelector(`[data-hf-wiz-input="${inputName}"]`);
      if (input) input.closest('.hf-wiz__field').classList.add('is-invalid');
    }

    // ─── Input bindings (text inputs + textareas) ──────────────
    const charCountEl = root.querySelector('[data-hf-wiz-char-count]');
    const charCountWrap = charCountEl ? charCountEl.parentElement : null;

    root.querySelectorAll('[data-hf-wiz-input]').forEach((input) => {
      const key = input.dataset.hfWizInput;
      const isTextarea = input.tagName === 'TEXTAREA';
      input.addEventListener('input', () => {
        state.answers[key] = input.value;
        input.closest('.hf-wiz__field').classList.remove('is-invalid');
        if (key === 'statementText' && charCountEl) {
          charCountEl.textContent = input.value.length;
          if (charCountWrap) charCountWrap.classList.toggle('is-warning', input.value.length > 900);
        }
        saveProgress();
      });
      input.addEventListener('keydown', (e) => {
        // Enter advances on text inputs, but NOT textareas (newlines are valid there)
        if (e.key === 'Enter' && !isTextarea && state.step < REVIEW_STEP) {
          e.preventDefault();
          handleNext();
        }
      });
    });

    // ─── Conditional reveal: statement text only when a style is picked (not "No") ─
    const statementTextWrap = root.querySelector('[data-hf-wiz-statement-text]');
    function updateStatementTextReveal() {
      if (!statementTextWrap) return;
      const picked = !!state.answers.statementGraphic && state.answers.statementGraphic !== 'No';
      statementTextWrap.hidden = !picked;
      if (!picked) {
        // If they switch from Yes to No, clear the text so it's not in the order
        if (state.answers.statementText) {
          state.answers.statementText = '';
          const ta = root.querySelector('[data-hf-wiz-input="statementText"]');
          if (ta) ta.value = '';
          if (charCountEl) charCountEl.textContent = '0';
          if (charCountWrap) charCountWrap.classList.remove('is-warning');
        }
      }
    }

    // ─── Card / button selections ──────────────────────────────
    root.querySelectorAll('[data-hf-wiz-select]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        // If user clicked the video preview button inside the card, don't select
        if (e.target.closest('[data-hf-wiz-video-preview]')) return;

        const key = btn.dataset.hfWizSelect;
        const value = btn.dataset.value;
        state.answers[key] = value;

        const siblings = root.querySelectorAll(`[data-hf-wiz-select="${key}"]`);
        siblings.forEach((s) => s.classList.remove('is-selected'));
        btn.classList.add('is-selected');

        // Conditional reveal: statement text input only when Yes is picked
        if (key === 'statementGraphic') updateStatementTextReveal();

        updateRunningTotal();
        saveProgress();

        // Pause auto-advance on Step 12 if user picked Yes (they need to type the statement first)
        const pauseAutoAdvance = (key === 'statementGraphic' && value !== 'No');

        if (state.step < REVIEW_STEP && !pauseAutoAdvance) {
          setTimeout(() => {
            if (validateStep(state.step)) goToStep(state.step + 1);
          }, 380);
        } else if (pauseAutoAdvance) {
          // Focus the textarea so they can start typing immediately
          setTimeout(() => {
            const ta = root.querySelector('[data-hf-wiz-input="statementText"]');
            if (ta) ta.focus();
          }, 400);
        }
      });
    });

    // ─── Signature pad (draw-in-browser signature) ─────────────
    // Requires the signature_pad library loaded from CDN before this script.
    // Wires each signature step's <canvas> to a save button that converts
    // the drawing to a PNG blob and uploads it through the same Cloudinary
    // path as the photo upload, storing the URL under signaturePhotoUrl.
    root.querySelectorAll('[data-hf-wiz-signature]').forEach((sigWrap) => {
      const canvas = sigWrap.querySelector('[data-hf-wiz-sig-canvas]');
      const clearBtn = sigWrap.querySelector('[data-hf-wiz-sig-clear]');
      const saveBtn = sigWrap.querySelector('[data-hf-wiz-sig-save]');
      const statusEl = sigWrap.querySelector('[data-hf-wiz-sig-status]');
      const errorEl = sigWrap.querySelector('[data-hf-wiz-sig-error]');
      const thicknessEl = sigWrap.querySelector('[data-hf-wiz-sig-thickness]');
      if (!canvas || typeof SignaturePad === 'undefined') return;

      const sigPad = new SignaturePad(canvas, {
        backgroundColor: '#ffffff',
        penColor: '#000000',
        minWidth: 1.5,
        maxWidth: 3.5
      });

      function resizeCanvas() {
        const wrap = canvas.parentElement;
        const w = wrap.clientWidth;
        if (w === 0) return; // step is hidden, wait for it to become visible
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const data = sigPad.toData();
        canvas.width = w * ratio;
        canvas.height = 240 * ratio;
        canvas.style.width = w + 'px';
        canvas.style.height = '240px';
        canvas.getContext('2d').scale(ratio, ratio);
        sigPad.clear();
        if (data && data.length) sigPad.fromData(data);
      }
      function updateThickness(v) {
        const val = parseFloat(v);
        sigPad.minWidth = Math.max(0.5, val * 0.3);
        sigPad.maxWidth = Math.max(1, val * 0.7);
      }

      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);
      // Re-run resize when the parent step becomes visible (starts at 0 width).
      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(() => resizeCanvas());
        ro.observe(canvas.parentElement);
      }

      if (thicknessEl) {
        updateThickness(thicknessEl.value);
        thicknessEl.addEventListener('input', (e) => updateThickness(e.target.value));
      }

      clearBtn.addEventListener('click', () => {
        sigPad.clear();
        if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
        if (statusEl) { statusEl.hidden = true; statusEl.textContent = ''; }
      });

      saveBtn.addEventListener('click', async () => {
        if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
        if (sigPad.isEmpty()) {
          if (errorEl) { errorEl.textContent = 'Please draw your signature first.'; errorEl.hidden = false; }
          return;
        }
        if (!cfg.cloudinaryCloud || !cfg.cloudinaryPreset) {
          if (errorEl) { errorEl.textContent = 'Upload is not configured yet. Please contact support.'; errorEl.hidden = false; }
          return;
        }
        saveBtn.disabled = true;
        if (statusEl) { statusEl.textContent = 'Uploading signature…'; statusEl.hidden = false; }
        canvas.toBlob(async (blob) => {
          if (!blob) {
            saveBtn.disabled = false;
            if (errorEl) { errorEl.textContent = 'Could not export signature. Try again.'; errorEl.hidden = false; }
            return;
          }
          const formData = new FormData();
          formData.append('file', blob, 'signature-drawn.png');
          formData.append('upload_preset', cfg.cloudinaryPreset);
          try {
            const res = await fetch(`https://api.cloudinary.com/v1_1/${cfg.cloudinaryCloud}/image/upload`, {
              method: 'POST',
              body: formData
            });
            const data = await res.json();
            if (!res.ok || !data.secure_url) throw new Error('Upload failed');
            state.answers.signaturePhotoUrl = data.secure_url;
            state.answers.signaturePhotoName = 'signature-drawn.png';
            saveProgress();
            if (statusEl) { statusEl.textContent = '✓ Signature saved. You can continue.'; statusEl.hidden = false; }
          } catch (err) {
            if (errorEl) { errorEl.textContent = 'Upload failed. Please try again or use the photo upload option.'; errorEl.hidden = false; }
            if (statusEl) { statusEl.hidden = true; }
          } finally {
            saveBtn.disabled = false;
          }
        }, 'image/png');
      });
    });

    // ─── File uploads (Cloudinary) ─────────────────────────────
    // Warn at load time if Cloudinary isn't configured — otherwise the user
    // hits a dead upload button and only sees the error mid-flow.
    const cloudinaryReady = !!(cfg.cloudinaryCloud && cfg.cloudinaryPreset);
    root.querySelectorAll('[data-hf-wiz-upload]').forEach((uploadEl) => {
      const key = uploadEl.dataset.hfWizUpload;
      const fileInput = uploadEl.querySelector('[data-hf-wiz-upload-input]');
      const empty = uploadEl.querySelector('.hf-wiz__upload-empty');
      const progress = uploadEl.querySelector('.hf-wiz__upload-progress');
      const progressBar = uploadEl.querySelector('[data-hf-wiz-upload-bar]');
      const progressStatus = uploadEl.querySelector('[data-hf-wiz-upload-status]');
      const preview = uploadEl.querySelector('.hf-wiz__upload-preview');
      const previewImg = uploadEl.querySelector('[data-hf-wiz-upload-preview]');
      const filenameEl = uploadEl.querySelector('[data-hf-wiz-upload-filename]');
      const removeBtn = uploadEl.querySelector('[data-hf-wiz-upload-remove]');
      const errorEl = uploadEl.querySelector('[data-hf-wiz-upload-error]');

      if (!cloudinaryReady) {
        uploadEl.classList.add('is-disabled');
        uploadEl.style.opacity = '0.5';
        uploadEl.style.pointerEvents = 'none';
        if (errorEl) {
          errorEl.textContent = 'File upload is not configured yet. Please contact support before ordering.';
          errorEl.hidden = false;
        }
      }

      uploadEl.addEventListener('click', (e) => {
        if (uploadEl.classList.contains('has-file')) return;
        if (e.target.closest('[data-hf-wiz-upload-remove]')) return;
        fileInput.click();
      });

      ['dragenter', 'dragover'].forEach((evt) => {
        uploadEl.addEventListener(evt, (e) => {
          e.preventDefault();
          uploadEl.classList.add('is-dragging');
        });
      });
      ['dragleave', 'drop'].forEach((evt) => {
        uploadEl.addEventListener(evt, (e) => {
          e.preventDefault();
          uploadEl.classList.remove('is-dragging');
        });
      });
      uploadEl.addEventListener('drop', (e) => {
        const file = e.dataTransfer.files && e.dataTransfer.files[0];
        if (file) handleFile(file);
      });

      fileInput.addEventListener('change', () => {
        const file = fileInput.files && fileInput.files[0];
        if (file) handleFile(file);
      });

      removeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        state.answers[key + 'Url'] = '';
        state.answers[key + 'Name'] = '';
        uploadEl.classList.remove('has-file');
        previewImg.src = '';
        filenameEl.textContent = '';
        fileInput.value = '';
        errorEl.hidden = true;
        preview.hidden = true;
        empty.style.display = '';
        saveProgress();
      });

      function handleFile(file) {
        errorEl.hidden = true;
        errorEl.textContent = '';

        if (!file.type.startsWith('image/')) { showError('Please upload an image file (JPG, PNG, HEIC).'); return; }
        if (file.size > 25 * 1024 * 1024) { showError('File is too large. Maximum size is 25MB.'); return; }
        if (!cfg.cloudinaryCloud || !cfg.cloudinaryPreset) { showError('Upload is not configured yet. Please contact support.'); return; }

        empty.style.display = 'none';
        progress.hidden = false;
        uploadEl.dataset.uploading = 'true';
        progressBar.style.width = '0%';
        progressStatus.textContent = 'Uploading…';

        const url = `https://api.cloudinary.com/v1_1/${cfg.cloudinaryCloud}/image/upload`;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_preset', cfg.cloudinaryPreset);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            progressBar.style.width = pct + '%';
            progressStatus.textContent = `Uploading… ${pct}%`;
          }
        });

        xhr.addEventListener('load', () => {
          uploadEl.dataset.uploading = 'false';
          progress.hidden = true;
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const res = JSON.parse(xhr.responseText);
              if (res.secure_url) {
                state.answers[key + 'Url'] = res.secure_url;
                state.answers[key + 'Name'] = file.name;
                previewImg.src = res.secure_url;
                filenameEl.innerHTML = '<span class="hf-wiz__upload-success">Uploaded</span> ' + escapeHTML(file.name);
                uploadEl.classList.add('has-file');
                preview.hidden = false;
                saveProgress();
                return;
              }
            } catch (err) { /* fall through */ }
            showError('Upload succeeded but response was unexpected. Please try again.');
            empty.style.display = '';
          } else {
            showError(`Upload failed (${xhr.status}). Please try again.`);
            empty.style.display = '';
          }
        });

        xhr.addEventListener('error', () => {
          uploadEl.dataset.uploading = 'false';
          progress.hidden = true;
          empty.style.display = '';
          showError('Upload failed. Please check your connection and try again.');
        });

        xhr.send(formData);
      }

      function showError(msg) {
        errorEl.textContent = msg;
        errorEl.hidden = false;
      }
    });

    // ─── Prev / Next / Reset ───────────────────────────────────
    function handleNext() {
      if (validateStep(state.step)) {
        goToStep(state.step + 1);
      } else if (nextBtn) {
        nextBtn.animate(
          [{transform:'translateX(0)'},{transform:'translateX(-4px)'},{transform:'translateX(4px)'},{transform:'translateX(0)'}],
          { duration: 240 }
        );
      }
    }

    if (prevBtn) prevBtn.addEventListener('click', () => goToStep(state.step - 1));
    if (nextBtn) nextBtn.addEventListener('click', handleNext);

    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        if (confirm('Start over? Your progress will be cleared.')) {
          clearProgress();
          window.location.reload();
        }
      });
    }

    // ─── Pricing & review ──────────────────────────────────────
    function calcTotal() {
      let total = cfg.basePrice;
      if (state.answers.jerseySwap === 'Yes') total += cfg.jerseyPrice;
      if (state.answers.statementGraphic && state.answers.statementGraphic !== 'No') total += cfg.statementPrice;
      if (state.answers.rushTurnaround === 'Yes') total += cfg.rushPrice;
      return total;
    }
    function fmt(n) { return '$' + n.toFixed(2); }

    function updateRunningTotal() {
      if (!runningTotalEl) return;
      const total = calcTotal();
      runningTotalEl.textContent = fmt(total);
      if (lastTotal !== null && total !== lastTotal && runningTotalWrap) {
        runningTotalWrap.classList.remove('is-bumped');
        // force reflow so animation can restart
        void runningTotalWrap.offsetWidth;
        runningTotalWrap.classList.add('is-bumped');
      }
      lastTotal = total;
    }

    function renderReview() {
      const a = state.answers;
      const athleteRows = [
        ['Name', `${a.firstName} ${a.lastName}`.trim()],
        ['Email', a.email], ['Sport', a.sport], ['Position', a.position],
        ['School', a.school], ['Conference', a.conference], ['Class of', a.gradClass]
      ];
      const designRows = [
        ['Graphic Style', a.graphicStyle],
        ['Jersey Swap', a.jerseySwap || 'No'],
        ['Statement Graphic', a.statementGraphic || 'No'],
        ['Rush Delivery', a.rushTurnaround || 'No']
      ];
      if (a.statementGraphic && a.statementGraphic !== 'No' && a.statementText) {
        designRows.push(['Statement Text', a.statementText]);
      }
      const fileRows = [
        ['Athlete Photo', a.athletePhotoName || (a.athletePhotoUrl ? 'Uploaded' : '—')],
        ['Additional Photo', a.additionalPhotoName || (a.additionalPhotoUrl ? 'Uploaded' : '—')]
      ];
      const pricingRows = [['Commitment Graphic', fmt(cfg.basePrice)]];
      if (a.jerseySwap === 'Yes') pricingRows.push(['Jersey Swap', '+' + fmt(cfg.jerseyPrice)]);
      if (a.statementGraphic && a.statementGraphic !== 'No') pricingRows.push(['Statement Graphic', '+' + fmt(cfg.statementPrice)]);
      if (a.rushTurnaround === 'Yes') pricingRows.push(['Rush Delivery', '+' + fmt(cfg.rushPrice)]);

      paintRows(root.querySelector('[data-hf-wiz-review="athlete"]'), athleteRows);
      paintRows(root.querySelector('[data-hf-wiz-review="design"]'), designRows);
      paintRows(root.querySelector('[data-hf-wiz-review="files"]'), fileRows);
      paintRows(root.querySelector('[data-hf-wiz-review="pricing"]'), pricingRows);
      if (totalEl) totalEl.textContent = fmt(calcTotal());
    }

    function paintRows(container, rows) {
      if (!container) return;
      container.innerHTML = '';
      rows.forEach(([label, value]) => {
        const row = document.createElement('div');
        row.className = 'hf-wiz__review-row';
        row.innerHTML = `<dt>${escapeHTML(label)}</dt><dd>${escapeHTML(value || '—')}</dd>`;
        container.appendChild(row);
      });
    }

    function escapeHTML(s) {
      return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#039;');
    }

    // ─── Submit to Shopify cart ────────────────────────────────
    if (submitBtn) submitBtn.addEventListener('click', submitOrder);

    async function submitOrder() {
      if (!cfg.baseVariantId) {
        showSubmitError('Base product is not configured. Please contact support.');
        return;
      }
      submitBtn.disabled = true;
      submitBtn.classList.add('is-loading');
      submitErrorEl.hidden = true;
      submitErrorEl.textContent = '';

      const a = state.answers;
      const properties = {
        'First Name': a.firstName, 'Last Name': a.lastName, 'Email': a.email,
        'Sport': a.sport, 'Position(s)': a.position,
        'Commitment School': a.school, 'Conference & Division': a.conference,
        'Graduating Class': a.gradClass,
        'Graphic Style': a.graphicStyle,
        'Jersey Swap': a.jerseySwap || 'No',
        'Statement Graphic': a.statementGraphic || 'No',
        'Rush Delivery': a.rushTurnaround || 'No'
      };
      if (a.jerseyNumber) properties['Jersey Number'] = a.jerseyNumber;
      if (a.statementGraphic && a.statementGraphic !== 'No' && a.statementText) {
        properties['Statement Text'] = a.statementText;
      }
      if (a.athletePhotoUrl) properties['Athlete Photo'] = a.athletePhotoUrl;
      if (a.athletePhotoName) properties['Athlete Photo Filename'] = a.athletePhotoName;
      if (a.additionalPhotoUrl) properties['Additional Photo'] = a.additionalPhotoUrl;
      if (a.additionalPhotoName) properties['Additional Photo Filename'] = a.additionalPhotoName;
      if (a.signaturePhotoUrl) properties['Signature Photo'] = a.signaturePhotoUrl;
      if (a.signaturePhotoName) properties['Signature Photo Filename'] = a.signaturePhotoName;

      const items = [{ id: parseInt(cfg.baseVariantId, 10), quantity: 1, properties }];
      if (a.jerseySwap === 'Yes' && cfg.jerseyVariantId) items.push({ id: parseInt(cfg.jerseyVariantId, 10), quantity: 1 });
      if (a.statementGraphic && a.statementGraphic !== 'No' && cfg.statementVariantId) items.push({ id: parseInt(cfg.statementVariantId, 10), quantity: 1 });
      if (a.rushTurnaround === 'Yes' && cfg.rushVariantId) items.push({ id: parseInt(cfg.rushVariantId, 10), quantity: 1 });

      try {
        await fetch('/cart/clear.js', { method: 'POST' });
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!res.ok) {
          const errText = await res.text();
          let errMsg = "We couldn't add your order to cart. Please try again.";
          try {
            const errJson = JSON.parse(errText);
            if (errJson.description) errMsg = errJson.description;
            else if (errJson.message) errMsg = errJson.message;
          } catch (e) {}
          throw new Error(errMsg);
        }
        clearProgress(); // order submitted — fresh slate next visit
        window.location.href = '/checkout';
      } catch (err) {
        submitBtn.disabled = false;
        submitBtn.classList.remove('is-loading');
        showSubmitError(err.message || 'Something went wrong. Please try again.');
      }
    }

    function showSubmitError(msg) {
      if (!submitErrorEl) return;
      submitErrorEl.textContent = msg;
      submitErrorEl.hidden = false;
    }

    // ─── Init ──────────────────────────────────────────────────
    // Show initial total immediately (suppresses the bump animation for the first render)
    lastTotal = calcTotal();
    if (runningTotalEl) runningTotalEl.textContent = fmt(lastTotal);

    const saved = loadProgress();
    if (saved && saved.answers) {
      Object.assign(state.answers, saved.answers);
      restoreUI();
      const startStep = Math.max(1, Math.min(TOTAL_STEPS, saved.step || 1));
      goToStep(startStep);
    } else {
      goToStep(1);
    }
  }

  function bootAll() {
    document.querySelectorAll('[data-hf-wiz]').forEach(initWizard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  document.addEventListener('shopify:section:load', bootAll);
})();
